import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Header from '../components/Header';
import SearchResults from '../components/SearchResults';
import ProductSidebar from '../components/ProductSidebar';
import { getHistoryById, searchByUpload } from '../services/api';
import '../App.css';

/**
 * ResultsPage Component
 * Displays search results along with original and enhanced images
 * Can be used for fresh searches or viewing history
 */
const ResultsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: historyId } = useParams();

  // Get data from navigation state (for fresh searches)
  const {
    originalImage: stateOriginalImage,
    enhancedImage: stateEnhancedImage,
    backgroundRemovedImage: stateBackgroundRemovedImage,
    showBackgroundRemoved: initialShowBg,
    searchResults: stateSearchResults,
    historyItem: stateHistoryItem,
  } = location.state || {};

  // Local state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [historyData, setHistoryData] = useState(stateHistoryItem || null);
  const [originalImage, setOriginalImage] = useState(stateOriginalImage);
  const [enhancedImage, setEnhancedImage] = useState(stateEnhancedImage);
  const [backgroundRemovedImage, setBackgroundRemovedImage] = useState(stateBackgroundRemovedImage);
  const [searchResults, setSearchResults] = useState(stateSearchResults);
  const [showBackgroundRemoved] = useState(initialShowBg || false);

  // Track which image was used for search ('original' or 'enhanced')
  const [activeSearchImage, setActiveSearchImage] = useState('enhanced');

  // State for sidebar
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Check if viewing from history
  const isHistoryView = !!historyId;

  // Load history data if viewing from history
  useEffect(() => {
    const loadHistoryData = async () => {
      if (historyId && !historyData) {
        setLoading(true);
        setError(null);

        try {
          const response = await getHistoryById(historyId);
          const history = response.search;
          setHistoryData(history);

          // Extract image URLs from query
          if (history.query?.originalImageUrl) {
            setOriginalImage(history.query.originalImageUrl);
          } else if (history.query?.imageUrl) {
            // Fallback for old history entries
            setOriginalImage(history.query.imageUrl);
          }

          if (history.query?.enhancedImageUrl) {
            setEnhancedImage(history.query.enhancedImageUrl);
          }

          if (history.query?.backgroundRemovedImageUrl) {
            setBackgroundRemovedImage(history.query.backgroundRemovedImageUrl);
          }

          // Convert history results to display format
          const results = history.results.map((result) => ({
            sku: result.sku,
            confidence: result.similarity || result.score || 0,
            category: result.category || 'Jewelry',
            metadata: {
              name: result.name,
              metal: result.metal,
              gemstone: result.gemstone,
              description: result.description,
            },
            imageUrl: result.imageUrl,
          }));

          setSearchResults(results);
        } catch (err) {
          console.error('Failed to load history:', err);
          setError(err.message || 'Failed to load search history');
        } finally {
          setLoading(false);
        }
      }
    };

    loadHistoryData();
  }, [historyId, historyData]);

  // Redirect if no data and not loading
  useEffect(() => {
    if (!loading && !searchResults && !historyId) {
      navigate('/');
    }
  }, [searchResults, loading, historyId, navigate]);

  // Return early if no data
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#B8A88F' }}>
        <Header />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 20px',
          gap: '16px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ fontSize: '16px', color: '#6b7280', margin: 0 }}>Loading search results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#B8A88F' }}>
        <Header />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 20px',
          gap: '16px',
          textAlign: 'center'
        }}>
          <span style={{ fontSize: '48px' }}>⚠️</span>
          <p style={{ fontSize: '16px', color: '#dc2626', margin: 0 }}>{error}</p>
          <button
            onClick={() => navigate(isHistoryView ? '/history' : '/')}
            style={{
              marginTop: '16px',
              padding: '12px 24px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!searchResults) {
    return null;
  }

  const handleBackToSearch = () => {
    if (isHistoryView) {
      navigate('/history');
    } else {
      navigate('/');
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setIsSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
    // Optionally delay clearing the product to allow animation to finish
    setTimeout(() => setSelectedProduct(null), 300);
  };

  /**
   * Convert base64 data URL to Blob
   */
  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  /**
   * Process and normalize search results (same logic as useImageSearch hook)
   */
  const processSearchResults = (data) => {
    // Handle different response formats
    let resultsArray = [];

    if (Array.isArray(data)) {
      resultsArray = data;
    } else if (data && Array.isArray(data.results)) {
      resultsArray = data.results;
    } else if (data && Array.isArray(data.matches)) {
      resultsArray = data.matches;
    } else if (data && data.result) {
      resultsArray = [data.result];
    }

    // Normalize result objects
    return resultsArray.map((result, index) => {
      // Calculate confidence as percentage
      // score is similarity (0-1 range, higher is better) from Matching Engine
      let confidence = 0;
      if (result.score !== undefined) {
        // score is similarity value (0-1), convert to percentage
        // Higher score = better match (0.9999 = 99.99% for exact match)
        confidence = result.score * 100;
      } else if (result.similarity !== undefined && result.similarity !== null) {
        // Fallback to similarity field if score not available
        confidence = result.similarity;
      } else if (result.confidence !== undefined) {
        confidence = result.confidence;
      }

      return {
        sku: result.sku || result.id || result.productId || `ITEM-${index + 1}`,
        confidence: confidence,
        category: result.category || result.type || null,
        metadata: result.metadata || result.details || result.attributes || {},
        imageUrl: result.imageUrl,
        // Preserve original data
        ...result,
      };
    });
  };

  /**
   * Handle thumbnail click to redo search with selected image
   */
  const handleThumbnailClick = async (imageType) => {
    // Don't allow toggling if viewing from history or if already using that image
    if (isHistoryView || imageType === activeSearchImage || loading) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let imageToSend;

      if (imageType === 'original' && originalImage) {
        // For original, convert base64 to blob (we don't have the original File here)
        // The backend will use file.buffer directly when searchedImageType === 'original'
        imageToSend = dataURLtoBlob(originalImage);
      } else if (imageType === 'enhanced' && enhancedImage) {
        imageToSend = dataURLtoBlob(enhancedImage);
      } else {
        throw new Error('Image not available');
      }

      console.log(`Redoing search with ${imageType} image...`);

      // Pass all image URLs for saving to history
      const imageUrls = {
        originalImageUrl: originalImage,
        enhancedImageUrl: enhancedImage,
        backgroundRemovedImageUrl: backgroundRemovedImage,
        searchedImageType: imageType  // Track which image was actually searched ('original' or 'enhanced')
      };

      console.log('🔍 Frontend sending imageUrls:', {
        hasOriginal: !!originalImage,
        hasEnhanced: !!enhancedImage,
        hasBackgroundRemoved: !!backgroundRemovedImage,
        searchedImageType: imageType
      });

      // Perform the search
      const response = await searchByUpload(imageToSend, `search-${imageType}.jpg`, imageUrls);

      // Process results through normalization (same as useImageSearch hook)
      const processedResults = processSearchResults(response);

      console.log(`Received ${processedResults.length} results for ${imageType} image`);

      // Update results and active image
      setSearchResults(processedResults);
      setActiveSearchImage(imageType);
    } catch (err) {
      console.error('Failed to redo search:', err);
      setError(err.message || 'Failed to search. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Styles
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#B8A88F',
    },
    main: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '40px 20px',
    },
    headerBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '24px',
      marginBottom: '32px',
      paddingBottom: '20px',
      borderBottom: '2px solid #e5e7eb',
    },
    backButton: {
      padding: '10px 20px',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease',
      flexShrink: 0,
    },
    titleSection: {
      flex: '1',
      marginLeft: '24px',
      textAlign: 'center',
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#1f2937',
      margin: 0,
    },
    subtitle: {
      fontSize: '16px',
      color: '#6b7280',
      margin: '4px 0 0 0',
    },
    thumbnailsSection: {
      display: 'flex',
      gap: '16px',
      alignItems: 'center',
      backgroundColor: '#f9fafb',
      padding: '12px 16px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      flexShrink: 0,
    },
    thumbnailWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '6px',
      cursor: 'pointer',
      transition: 'transform 0.2s ease',
    },
    thumbnailLabel: {
      fontSize: '11px',
      fontWeight: '600',
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
    },
    thumbnail: {
      width: '80px',
      height: '80px',
      borderRadius: '8px',
      objectFit: 'cover',
      border: '2px solid #e5e7eb',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s ease',
    },
    thumbnailActive: {
      border: '3px solid #B8A88F',
      boxShadow: '0 4px 12px rgba(184, 168, 143, 0.4)',
      transform: 'scale(1.05)',
    },
    thumbnailDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    resultsSection: {
      marginTop: '20px',
    },
  };

  const displayedEnhancedImage = (showBackgroundRemoved && backgroundRemovedImage)
    ? backgroundRemovedImage
    : enhancedImage;

  const hasImages = originalImage || enhancedImage;
  const backButtonText = isHistoryView ? '← Back to History' : '← Back to Search';

  return (
    <div style={styles.container}>
      <Header onHistoryClick={() => navigate('/history')} />

      <main style={styles.main}>
        {/* Header Bar with Back Button, Title, and Thumbnails */}
        <div style={styles.headerBar}>
          {/* Back Button */}
          <button
            style={styles.backButton}
            onClick={handleBackToSearch}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#f3f4f6';
            }}
          >
            {backButtonText}
          </button>

          {/* Title Section */}
          <div style={styles.titleSection}>
            <h2 style={styles.title}>Search Results</h2>
            <p style={styles.subtitle}>
              Showing {Math.min(searchResults.length, 8)} of {searchResults.length} similar {searchResults.length === 1 ? 'item' : 'items'}
            </p>
          </div>

          {/* Thumbnails Section - Only show if images are available */}
          {hasImages && !isHistoryView && (
            <div style={styles.thumbnailsSection}>
              {/* Original Image Thumbnail */}
              {originalImage && (
                <div
                  style={{
                    ...styles.thumbnailWrapper,
                    ...(loading ? styles.thumbnailDisabled : {}),
                  }}
                  onClick={() => handleThumbnailClick('original')}
                  onMouseEnter={(e) => {
                    if (!loading && activeSearchImage !== 'original') {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                  title={activeSearchImage === 'original' ? 'Currently using this image' : 'Click to search with original image'}
                >
                  <div style={{
                    ...styles.thumbnailLabel,
                    color: activeSearchImage === 'original' ? '#B8A88F' : '#6b7280',
                  }}>
                    Original
                  </div>
                  <img
                    src={originalImage}
                    alt="Original thumbnail"
                    style={{
                      ...styles.thumbnail,
                      ...(activeSearchImage === 'original' ? styles.thumbnailActive : {}),
                    }}
                  />
                </div>
              )}

              {/* Enhanced Image Thumbnail */}
              {displayedEnhancedImage && (
                <div
                  style={{
                    ...styles.thumbnailWrapper,
                    ...(loading ? styles.thumbnailDisabled : {}),
                  }}
                  onClick={() => handleThumbnailClick('enhanced')}
                  onMouseEnter={(e) => {
                    if (!loading && activeSearchImage !== 'enhanced') {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                  title={activeSearchImage === 'enhanced' ? 'Currently using this image' : 'Click to search with enhanced image'}
                >
                  <div style={{
                    ...styles.thumbnailLabel,
                    color: activeSearchImage === 'enhanced' ? '#B8A88F' : '#6b7280',
                  }}>
                    {showBackgroundRemoved ? 'No BG' : 'Enhanced'}
                  </div>
                  <img
                    src={displayedEnhancedImage}
                    alt="Enhanced thumbnail"
                    style={{
                      ...styles.thumbnail,
                      ...(activeSearchImage === 'enhanced' ? styles.thumbnailActive : {}),
                    }}
                  />
                </div>
              )}
            </div>
          )}
          {/* Show thumbnails for history view but not clickable */}
          {hasImages && isHistoryView && (
            <div style={styles.thumbnailsSection}>
              {/* Original Image Thumbnail */}
              {originalImage && (
                <div style={styles.thumbnailWrapper}>
                  <div style={styles.thumbnailLabel}>Original</div>
                  <img
                    src={originalImage}
                    alt="Original thumbnail"
                    style={styles.thumbnail}
                  />
                </div>
              )}

              {/* Enhanced Image Thumbnail */}
              {displayedEnhancedImage && (
                <div style={styles.thumbnailWrapper}>
                  <div style={styles.thumbnailLabel}>
                    {showBackgroundRemoved ? 'No BG' : 'Enhanced'}
                  </div>
                  <img
                    src={displayedEnhancedImage}
                    alt="Enhanced thumbnail"
                    style={styles.thumbnail}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Results Section */}
        <section style={styles.resultsSection}>
          {loading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '80px 20px',
              gap: '16px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid #e5e7eb',
                borderTopColor: '#B8A88F',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ fontSize: '16px', color: '#6b7280', margin: 0 }}>
                Searching with {activeSearchImage} image...
              </p>
            </div>
          ) : (
            <SearchResults
              results={searchResults}
              loading={false}
              onProductClick={handleProductClick}
            />
          )}
        </section>
      </main>

      {/* Product Sidebar */}
      <ProductSidebar
        product={selectedProduct}
        isOpen={isSidebarOpen}
        onClose={handleCloseSidebar}
      />
    </div>
  );
};

export default ResultsPage;
